import { useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { getToken } from '../../../services/tokenService';
import { PAYS_OPTIONS, TERRITOIRES_OPTIONS } from '../constants';

const API = '/api';

export default function useSubscriptionCreation({
  formData, selectedClient, calculData, montantTotalHT, montantTTC,
  souscriptionCreee, setSouscriptionCreee, setEtape,
  persistence, sig, docs, isNominative: isNominative,
}) {
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  const validateField = useCallback((fieldName, value) => {
    let error = null;
    switch (fieldName) {
      case 'raison_sociale': if (!value?.trim()) error = 'Requis'; break;
      case 'siret': {
        const c = value?.replace(/\s/g, '') || '';
        if (!c) error = 'Requis'; else if (!/^\d{14}$/.test(c)) error = '14 chiffres requis';
        break;
      }
      case 'telephone': {
        const t = value?.replace(/\s/g, '') || '';
        if (!t) error = 'Requis pour SMS'; else if (!/^\d{9,10}$/.test(t)) error = '9-10 chiffres';
        break;
      }
      case 'email': if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) error = 'Format invalide'; break;
      case 'code_postal': if (value && !/^\d{5}$/.test(value)) error = '5 chiffres'; break;
      case 'code_naf': if (value && !/^[0-9]{2}\.[0-9]{2}[A-Z]$/i.test(value) && !/^[0-9]{4}[A-Z]$/i.test(value)) error = 'Format: 43.99C'; break;
      case 'effectif': if (!value || parseInt(value) < 0) error = 'Requis'; break;
      default: break;
    }
    setFieldErrors(prev => ({ ...prev, [fieldName]: error }));
    return error;
  }, []);

  const validateFormData = useCallback(() => {
    const errors = [];
    if (!formData.raison_sociale?.trim()) errors.push('Raison sociale manquante');
    const siretClean = formData.siret?.replace(/\s/g, '') || '';
    if (!siretClean) errors.push('SIRET manquant');
    else if (!/^\d{14}$/.test(siretClean)) errors.push('SIRET invalide (14 chiffres requis)');
    if (!formData.effectif || parseInt(formData.effectif) < 0) errors.push('Effectif manquant ou invalide');
    const telClean = formData.telephone?.replace(/\s/g, '') || '';
    if (!telClean) errors.push('Telephone manquant (requis pour la signature SMS)');
    else if (!/^\d{9,10}$/.test(telClean)) errors.push('Numero de telephone invalide (9-10 chiffres)');
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) errors.push('Format email invalide');
    if (formData.code_postal && !/^\d{5}$/.test(formData.code_postal)) errors.push('Code postal invalide (5 chiffres)');
    if (formData.code_naf && !/^[0-9]{2}\.[0-9]{2}[A-Z]$/.test(formData.code_naf.toUpperCase()) && !/^[0-9]{4}[A-Z]$/.test(formData.code_naf.toUpperCase())) errors.push('Code NAF invalide (format: 43.99C)');
    return errors;
  }, [formData]);

  const creerSouscription = async () => {
    const validationErrors = validateFormData();
    if (validationErrors.length > 0) {
      validationErrors.forEach((error, index) => {
        setTimeout(() => toast.error(error, { duration: 5000 }), index * 300);
      });
      return;
    }
    setLoading(true);
    try {
      const token = getToken();
      const telephoneComplet = formData.telephone
        ? `${formData.indicatif_telephone}${formData.telephone.replace(/^0/, '')}`
        : '';
      const territoireInfo = TERRITOIRES_OPTIONS.find(t => t.id === formData.territoire);
      const paysInfo = PAYS_OPTIONS.find(p => p.code === formData.pays);

      // Multi-territoires : on construit la liste détaillée (id, nom, ca)
      const territoiresList = Array.isArray(formData.territoires) && formData.territoires.length > 0
        ? formData.territoires
        : (formData.territoire ? [formData.territoire] : []);
      const territoiresCA = formData.ca_modifie_workflow
        ? Object.fromEntries(Object.entries(formData.territoires_ca || {}).filter(([k]) => territoiresList.includes(k)))
        : (formData.territoires_ca || {});
      const territoiresDetailed = territoiresList.map(id => {
        const info = TERRITOIRES_OPTIONS.find(t => t.id === id);
        return {
          id,
          nom: info?.nom || id,
          zone: info?.zone || '',
          ca: Number(territoiresCA[id] || 0),
        };
      });

      const souscriptionData = {
        raison_sociale: formData.raison_sociale.trim(),
        siret: formData.siret.trim(),
        code_naf: formData.code_naf || '',
        forme_juridique: formData.forme_juridique || '',
        statut_juridique: formData.forme_juridique || '',
        nom_gerant: formData.nom_gerant || '',
        prenom_gerant: formData.prenom_gerant || '',
        fonction_gerant: formData.fonction_gerant || 'Gerant',
        adresse_entreprise: formData.adresse_entreprise || '',
        code_postal: formData.code_postal || '',
        ville: formData.ville || '',
        pays: paysInfo?.nom || 'France',
        pays_code: formData.pays || 'FR',
        territoire: territoireInfo?.nom || 'France Metropolitaine',
        territoire_id: formData.territoire || 'metropole',
        // V36 — Multi-territoires
        territoires: territoiresList,
        territoires_detailed: territoiresDetailed,
        territoires_ca: territoiresCA,
        chiffre_affaires: parseFloat(formData.chiffre_affaires) || 0,
        effectif: parseInt(formData.effectif) || 0,
        date_creation_entreprise: formData.date_creation_entreprise || '',
        montant_ht_contribution: montantTotalHT || 0,
        type_souscription: calculData?.type_souscription || 'annuel',
        ...(calculData?.nombre_jours && { nombre_jours: calculData.nombre_jours }),
        ...(calculData?.operation_nominative && {
          operation_nominative: {
            type_operation: calculData.operation_nominative.type_operation || null,
            description_operation: calculData.operation_nominative.description_operation || null,
            adresse_operation: calculData.operation_nominative.adresse_operation || null,
            code_postal_operation: calculData.operation_nominative.code_postal_operation || null,
            ville_operation: calculData.operation_nominative.ville_operation || null,
            pays_operation: calculData.operation_nominative.pays_operation || 'France',
            territoire_intervention: calculData.operation_nominative.territoire_intervention || null
          }
        }),
        telephone: telephoneComplet,
        telephone_indicatif: formData.indicatif_telephone,
        email: formData.email || '',
        site_web: formData.site_web || '',
        activites_selectionnees: persistence.isResuming
          ? souscriptionCreee?.activites_selectionnees
          : (calculData?.activites_selectionnees || {}),
        activites_details: persistence.isResuming
          ? souscriptionCreee?.activites_details
          : (calculData?.activites_details || null),
        maitre_ouvrage: {
          type: formData.maitre_ouvrage_type,
          raison_sociale: formData.maitre_ouvrage_raison_sociale || '',
          siret: formData.maitre_ouvrage_siret || '',
          nom: formData.maitre_ouvrage_nom || '',
          prenom: formData.maitre_ouvrage_prenom || '',
          adresse: formData.maitre_ouvrage_adresse || '',
          code_postal: formData.maitre_ouvrage_code_postal || '',
          ville: formData.maitre_ouvrage_ville || '',
          pays: formData.maitre_ouvrage_pays || 'France',
          telephone: formData.maitre_ouvrage_telephone || '',
          email: formData.maitre_ouvrage_email || '',
          pieces_identite: formData.maitre_ouvrage_pieces_identite || []
        },
        tarification: {
          montant_ht: montantTotalHT,
          montant_ttc: montantTTC,
          taxes: calculData?.tarification?.taxes || calculData?.taxes_personnalisees
            ? { detail: calculData?.taxes_personnalisees || calculData?.tarification?.taxes?.detail, is_personnalise: true }
            : { detail: [
                { nom: 'Structuration cooperatif mutualise', taux: 9.5, montant: Math.round(montantTotalHT * 0.095 * 100) / 100 },
                { nom: 'Expertise preventive', taux: 9.5, montant: Math.round(montantTotalHT * 0.095 * 100) / 100 },
                { nom: "Fonds d'entraide FOGEDOM", taux: 9.5, montant: Math.round(montantTotalHT * 0.095 * 100) / 100 },
                { nom: 'Fonds interne cooperatif FOGEDOM-SCIC', taux: 9.5, montant: Math.round(montantTotalHT * 0.095 * 100) / 100 }
              ], total_taxes: Math.round(montantTotalHT * 0.38 * 100) / 100 },
          taxes_personnalisees: calculData?.taxes_personnalisees || null,
          ...(calculData?.montants_personnalises && { montants_personnalises: calculData.montants_personnalises })
        },
        ...(calculData?.taxes_personnalisees && {
          taxes_personnalisees: calculData.taxes_personnalisees,
          taxes: { detail: calculData.taxes_personnalisees, is_personnalise: true }
        }),
        ...(calculData?.periode_personnalisee && { periode_personnalisee: calculData.periode_personnalisee }),
        ...(calculData?.montants_personnalises && { montants_personnalises: calculData.montants_personnalises }),
        // Ventilation CA par territoire (panneau d'éligibilité — utilisée dans
        // les PDF Proposition / Devis / CP pour le bloc "CA par territoire")
        ...(!formData.ca_modifie_workflow && calculData?.territoires_ca && Object.keys(calculData.territoires_ca).length > 0 && {
          territoires_ca: calculData.territoires_ca
        })
      };
      // CA saisi dans le workflow : source unique pour Attestation, CP/CG et Devis ESS (cotisation inchangée)
      if (formData.ca_modifie_workflow) {
        const ca = parseFloat(formData.chiffre_affaires) || 0;
        souscriptionData.ca_modifie_workflow = true;
        souscriptionData.tarification.chiffre_affaires_ht = ca;
        if (souscriptionData.montants_personnalises) souscriptionData.montants_personnalises = { ...souscriptionData.montants_personnalises, ca_personnalise: ca };
        if (souscriptionData.tarification.montants_personnalises) souscriptionData.tarification.montants_personnalises = { ...souscriptionData.tarification.montants_personnalises, ca_personnalise: ca };
      }

      const sourceDevisId = new URLSearchParams(window.location.search).get('proposition');
      if (sourceDevisId) souscriptionData.source_devis_id = sourceDevisId;

      if (selectedClient !== 'nouveau' && formData.membre_id) {
        souscriptionData.membre_id = formData.membre_id;
      }

      const response = await axios.post(`${API}/souscriptions-rcd`, souscriptionData, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 120000,
      });

      const newSouscription = response.data;
      setSouscriptionCreee(newSouscription);
      sig.setTelephoneSignature(telephoneComplet);
      toast.success('Souscription creee !');

      docs.initDocsForSouscription(newSouscription.id);
      setEtape(4);
      docs.genererDocumentsAvecId(newSouscription.id).catch(() => {});
    } catch (error) {
      console.error('[SOUSCRIPTION] Erreur creation:', error?.response?.status, error?.response?.data, error?.message);
      const detail = error.response?.data?.detail;
      let msg = 'Erreur lors de la creation';
      if (error.code === 'ECONNABORTED') msg = 'Le serveur met trop de temps a repondre. Reessayez.';
      else if (!error.response) msg = 'Erreur reseau - verifiez votre connexion';
      else if (typeof detail === 'string') msg = detail;
      else if (Array.isArray(detail)) msg = detail.map(e => e.msg || JSON.stringify(e)).join(', ');
      else if (error.response?.status === 429) msg = 'Trop de requetes. Attendez quelques secondes et reessayez.';
      else if (error.response?.status >= 500) msg = `Erreur serveur (${error.response.status}). Contactez l'administrateur.`;
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return { loading, fieldErrors, validateField, creerSouscription };
}
